import { Image, Input, message } from 'antd';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import {
  MdOutlineAddPhotoAlternate,
  MdOutlineClose,
  MdSend,
} from 'react-icons/md';

import useMessages from '@/lib/hooks/useMessages';
import useUser from '@/lib/hooks/useUser';

import styles from './index.module.scss';
import Message from './Message';
import type { FixMessageProps, MessageType } from './types';

const FixMessage: React.FC<FixMessageProps> = ({ fix }) => {
  const [newMessage, setNewMessage] = useState('');
  const [currentUploadImage, setCurrentUploadImage] = useState<File | null>(
    null
  );

  const imgInputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  const {
    messages: rawMessages,
    sendMessage,
    fetchMore,
  } = useMessages('fix', fix._id);

  const { data: user } = useUser();

  const messages: MessageType[] = rawMessages.map((message) => ({
    id: message._id,
    type: message.sender.id === user?._id ? 'send' : 'receive',
    author: message.sender.name,
    time: moment(message.createdTime).format('YYYY-MM-DD HH:mm:ss'),
    content: message.content,
  }));

  const handleSendMessage = async () => {
    if (!newMessage && !currentUploadImage) {
      return;
    }
    try {
      if (currentUploadImage) {
        await sendMessage(currentUploadImage);
        setCurrentUploadImage(null);
        const input = imgInputRef.current;
        if (input) {
          input.value = '';
        }
      }
      if (newMessage) {
        await sendMessage(newMessage);
        setNewMessage('');
      }
      if (messagesRef.current) {
        messagesRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error('fix message send error', error);
      message.error(error.message);
    }
  };

  return (
    <div className={styles.fixMessage}>
      <div
        className={styles.messages}
        ref={messagesRef}
        onScroll={(e) => {
          const nativeTarget = e.nativeEvent.target;
          if (nativeTarget instanceof HTMLDivElement) {
            const { scrollTop, scrollHeight, offsetHeight } = nativeTarget;
            const scrollDistance = scrollTop > 0 ? scrollTop : -scrollTop;
            if (scrollHeight - offsetHeight - scrollDistance < 100) {
              fetchMore();
            }
          }
        }}
      >
        {messages.map((message) => (
          <Message key={message.id} message={message} />
        ))}
      </div>
      <div className={styles.inputs}>
        <label htmlFor="imageInput" className={styles.imgInputLabel}>
          <input
            id="imageInput"
            type="file"
            multiple={false}
            accept="image/*"
            hidden
            disabled={currentUploadImage !== null}
            ref={imgInputRef}
            onChange={(e) => {
              if (e?.target?.files?.[0]) {
                const file = e.target.files[0];
                if (file.size > 1024 * 1024 * 10) {
                  message.error('檔案大小不得超過 10MB');
                  return;
                }
                setCurrentUploadImage(file);
              }
            }}
          />
          <MdOutlineAddPhotoAlternate />
        </label>
        <Input
          value={newMessage}
          disabled={currentUploadImage !== null}
          onChange={(e) => setNewMessage(e.target.value)}
          onPressEnter={handleSendMessage}
        />
        <button onClick={handleSendMessage}>
          <MdSend />
        </button>
      </div>
      {currentUploadImage && (
        <div className={styles.uploadingImage}>
          <Image
            src={URL.createObjectURL(currentUploadImage)}
            alt="uploading"
            height={100}
          />
          <button
            className={styles.cancelButton}
            onClick={() => {
              setCurrentUploadImage(null);
              if (imgInputRef.current) {
                imgInputRef.current.value = '';
              }
            }}
          >
            <MdOutlineClose />
          </button>
        </div>
      )}
    </div>
  );
};

export default FixMessage;
